/**
 * AccountHealthBadge.tsx — per-account health FSM badge (D-18).
 *
 * Renders the current health state of an account. Dead/needs-reauth accounts
 * render visibly as DEAD/NEEDS REAUTH so chatters know they cannot act (D-06).
 *
 * States: connecting | online | reconnecting | cf_blocked | needs_reauth | dead
 */

import * as React from 'react';
import type { HealthState } from '@crm/types';

interface AccountHealthBadgeProps {
  state: HealthState | string;
  /** If true renders a smaller inline pill */
  compact?: boolean;
}

interface StateMeta {
  label: string;
  color: string;
  dot: string;
}

const STATE_META: Record<string, StateMeta> = {
  online: {
    label: 'Online',
    color: 'text-success border-success/40 bg-success/10',
    dot: 'bg-success',
  },
  connecting: {
    label: 'Connecting',
    color: 'text-info border-info/40 bg-info/10',
    dot: 'bg-info',
  },
  reconnecting: {
    label: 'Reconnecting',
    color: 'text-warning border-warning/40 bg-warning/10',
    dot: 'bg-warning animate-pulse',
  },
  cf_blocked: {
    label: 'CF Blocked',
    color: 'text-cf-blocked border-cf-blocked/40 bg-cf-blocked/10',
    dot: 'bg-cf-blocked',
  },
  needs_reauth: {
    label: 'Needs Re-auth',
    color: 'text-danger border-danger/40 bg-danger/10',
    dot: 'bg-danger animate-pulse',
  },
  dead: {
    label: 'DEAD',
    color: 'text-text-muted border-border bg-bg-card line-through',
    dot: 'bg-text-subtle',
  },
};

const UNKNOWN_META: StateMeta = {
  label: 'Unknown',
  color: 'text-text-subtle border-border bg-bg-card',
  dot: 'bg-text-subtle',
};

export function AccountHealthBadge({ state, compact = false }: AccountHealthBadgeProps) {
  const meta = STATE_META[state] ?? UNKNOWN_META;

  if (compact) {
    return (
      <span
        className={[
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border font-medium',
          meta.color,
        ].join(' ')}
        title={`Account health: ${meta.label}`}
      >
        <span className={['w-1.5 h-1.5 rounded-full shrink-0', meta.dot].join(' ')} />
        {meta.label}
      </span>
    );
  }

  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs border font-medium',
        meta.color,
      ].join(' ')}
      title={`Account health: ${meta.label}`}
    >
      <span className={['w-2 h-2 rounded-full shrink-0', meta.dot].join(' ')} />
      {meta.label}
    </span>
  );
}

/**
 * isAccountDead — returns true when the account health prevents sending (D-06).
 * Chatters cannot act on dead or needs_reauth accounts.
 */
export function isAccountDead(state: HealthState | string | null | undefined): boolean {
  return state === 'dead' || state === 'needs_reauth';
}
